import * as React from 'react';
import { css } from 'emotion';

import Form from '@components/library/form/Form';
import Label from '@components/library/labels/PrimaryLabel';
import InputContainer from '@components/library/input_container/InputContainer';
import { InputType, InputsMap, SelectionValue } from '@/components/library/inputs/Input.props';
import { CustomErrorMapping } from '@/components/library/form/Form.props';

import './styles/main.scss';
import Toggle from '@/components/library/toggle/BaseToggle';

type Map = { [id: number]: string };

interface CreateFtpIngestFormProps {
  sections: Map;
  collections: Map;
  gettyClient: boolean;
  children?: JSX.Element[];
}

const usernameName = 'username';
const passwordName = 'password';
const sectionSelectName = 'section';
const collectionSelectName = 'collection';
const allowFtpId = 'ftp-ingest-allow-ftp';
const usernameTakenErrors = ['file or folder already exists with that name', 'Username has already been taken'];
const badPasswordError = 'Password is among the most common passwords and therefore can not be used';

const onAsyncCustomValidation = async (inputsMap: InputsMap, allowFtp: boolean, setError: SetStateDispatch<boolean>):
  Promise<CustomErrorMapping[]> => {
  const { username, password, section, collection } = inputsMap;
  const collectionId = collection && ((collection.value) as SelectionValue).value;
  const sectionId = ((section.value) as SelectionValue).value;
  const body = {
    username: username.value,
    password: password.value,
    section: sectionId,
    collection: collectionId,
    ftp_permission: allowFtp
  };
  const url = `/${BFG.resource.slug}/bulk_management/ftp_ingest/create`;
  try {
    const { signal } = new AbortController();
    const options = {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${BF_Token}`, // eslint-disable-line @typescript-eslint/camelcase
        'Content-Type': 'application/json'
      },
      signal
    };
    const resp = await fetch(url, { ...options, body: JSON.stringify(body) });
    const json = resp.status === 202 || resp.status === 204 ? {} : await resp.json();
    const errorMapping: CustomErrorMapping[] = [];
    if (json.error) {
      if (usernameTakenErrors.includes(json.error)) {
        const inputName = usernameName;
        const errorString = t("This username is already taken");
        errorMapping.push({ inputName, errorString });
      }

      if (json.error === badPasswordError) {
        const inputName = passwordName;
        const errorString = t("Please select a less common password");
        errorMapping.push({ inputName, errorString });
      }

      setError(true);
    }

    return errorMapping;
  } catch (e) {
    throw e;
  }
};

const mapToOptions = (map: Map): { value: string; label: string }[] => {
  const keys = Object.keys(map);
  return keys.map((key) => ({ value: key, label: map[key] }));
};

const CreateFtpIngestForm = (props: CreateFtpIngestFormProps): JSX.Element => {
  const [error, setError] = React.useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [allowFtp, setAllowFtp] = React.useState(false);
  const toggleHeight = 26;
  const sectionOptions = mapToOptions(props.sections);
  const collectionOptions = mapToOptions(props.collections);
  const selectValidationRules = { isRequired: false };
  return (
    <div className="ftp-ingest-form">
      {props.gettyClient && (
        <div className="ftp-ingest-toggle-container">
          <Toggle
            isOn={allowFtp}
            onToggle={(): void => setAllowFtp(!allowFtp)}
            id={allowFtpId}
            sizes={{
              height: toggleHeight,
              width: 40,
              sliderDiameter: 24,
            }}
          />
          <Label
            attributes={{ htmlFor: allowFtpId, className: ['ftp-ingest-toggle-text', css({ lineHeight: `${toggleHeight}px` })].join(' ') }}
          >
            {`${t("Allow insecure")} FTP`}
          </Label>
        </div>
      )}
      <Form
        onAsyncCustomValidation={
          (inputsMap: InputsMap): Promise<CustomErrorMapping[]> => onAsyncCustomValidation(inputsMap, allowFtp, setError)
        }
        onSubmit={(): void => window.location.reload(true /* forceGET */)}
        submitButtonText={t("Create FTP Ingest")}
      >
        <InputContainer attributes={{ type: InputType.Text, name: usernameName }} />
        <InputContainer
          attributes={{
            type: InputType.Password,
            name: passwordName,
          }}
          validationRules={{ isRequired: true, minLength: 8 }}
          className="ftp-ingests-password-input-container"
        >
          <div className="ftp-ingests-password-warning">
            <div className="bff-warning ftp-ingests-password-warning-icon" />
            <div className="ftp-ingests-password-warning-details">
              <div>
                {t("Passwords can not be recovered or reset.")}
              </div>
              <div>
                {t("Please make a record of your password.")}
              </div>
            </div>
          </div>
        </InputContainer>
        <InputContainer
          attributes={{ type: InputType.Select, name: sectionSelectName }}
          selectValue={sectionOptions[0]}
          validationRules={selectValidationRules}
          options={sectionOptions}
          labelCopy={t("Add assets to section")}
        />
        {
          collectionOptions.length > 0
          && (
            <InputContainer
              attributes={{ type: InputType.Select, name: collectionSelectName }}
              validationRules={selectValidationRules}
              options={collectionOptions}
              labelCopy={t("Add assets to Collection (Optional)")}
            />
          )
        }
      </Form>
    </div>
  );
};

export default CreateFtpIngestForm;
